// PDF 组件专用 Promise.withResolvers polyfill
// 针对 react-pdf worker 线程和独立执行环境的强化版本

(() => {
  'use strict';
  
  // 获取所有可能的全局对象
  const getAllGlobalObjects = () => {
    const globals = [];
    
    // 主线程全局对象
    if (typeof globalThis !== 'undefined') globals.push(globalThis);
    if (typeof window !== 'undefined') globals.push(window);
    if (typeof global !== 'undefined') globals.push(global);
    if (typeof self !== 'undefined') globals.push(self);
    
    // Worker 线程特殊处理
    try {
      if (typeof WorkerGlobalScope !== 'undefined' && self instanceof WorkerGlobalScope) {
        globals.push(self);
      }
    } catch (e) {}
    
    // 尝试获取 worker 上下文
    try {
      if (typeof importScripts === 'function') {
        globals.push(self);
      }
    } catch (e) {}
    
    return [...new Set(globals)]; // 去重
  };
  
  // Promise.withResolvers polyfill 实现
  const createPolyfill = () => {
    return function withResolvers() {
      let resolve, reject;
      const promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
      });
      return { promise, resolve, reject };
    };
  };
  
  // 强制注入到所有全局对象
  const injectPolyfill = () => {
    const polyfill = createPolyfill();
    const globals = getAllGlobalObjects();
    let injectedCount = 0;
    
    globals.forEach((globalObj, index) => {
      try {
        if (!globalObj.Promise) {
          console.warn(`[PDF Polyfill] Global object ${index} has no Promise constructor`);
          return;
        }
        
        if (globalObj.Promise.withResolvers) {
          console.log(`[PDF Polyfill] Promise.withResolvers already exists in global ${index}`);
          return;
        }
        
        // 方法1: 直接赋值
        try {
          globalObj.Promise.withResolvers = polyfill;
          injectedCount++;
          console.log(`[PDF Polyfill] Method 1 success in global ${index}`);
        } catch (e) {
          console.warn(`[PDF Polyfill] Method 1 failed in global ${index}:`, e.message);
        }
        
        // 方法2: Object.defineProperty
        try {
          Object.defineProperty(globalObj.Promise, 'withResolvers', {
            value: polyfill,
            writable: true,
            configurable: true,
            enumerable: false
          });
          console.log(`[PDF Polyfill] Method 2 success in global ${index}`);
        } catch (e) {
          console.warn(`[PDF Polyfill] Method 2 failed in global ${index}:`, e.message);
        }
        
        // 方法3: Reflect.defineProperty (如果可用)
        try {
          if (typeof Reflect !== 'undefined' && Reflect.defineProperty) {
            Reflect.defineProperty(globalObj.Promise, 'withResolvers', {
              value: polyfill,
              writable: true,
              configurable: true
            });
            console.log(`[PDF Polyfill] Method 3 success in global ${index}`);
          }
        } catch (e) {
          console.warn(`[PDF Polyfill] Method 3 failed in global ${index}:`, e.message);
        }
        
        // 方法4: 原型链注入
        try {
          if (globalObj.Promise.prototype) {
            const PromiseConstructor = globalObj.Promise;
            if (!PromiseConstructor.withResolvers) {
              PromiseConstructor.withResolvers = polyfill;
            }
          }
        } catch (e) {
          console.warn(`[PDF Polyfill] Method 4 failed in global ${index}:`, e.message);
        }
        
      } catch (e) {
        console.error(`[PDF Polyfill] Failed to inject into global ${index}:`, e);
      }
    });
    
    console.log(`[PDF Polyfill] Injection completed. Injected into ${injectedCount} global objects.`);
    
    // 验证注入结果
    globals.forEach((globalObj, index) => {
      try {
        if (globalObj.Promise?.withResolvers) {
          const test = globalObj.Promise.withResolvers();
          if (test && typeof test.resolve === 'function' && typeof test.reject === 'function') {
            console.log(`[PDF Polyfill] Verification passed for global ${index}`);
          } else {
            console.error(`[PDF Polyfill] Verification failed for global ${index}: invalid structure`);
          }
        } else {
          console.error(`[PDF Polyfill] Verification failed for global ${index}: withResolvers not found`);
        }
      } catch (e) {
        console.error(`[PDF Polyfill] Verification error for global ${index}:`, e);
      }
    });
  };
  
  // 立即执行注入
  injectPolyfill();
  
  // 设置全局标记
  try {
    const globals = getAllGlobalObjects();
    globals.forEach(globalObj => {
      try {
        globalObj.__PDF_PROMISE_WITH_RESOLVERS_POLYFILL__ = true;
      } catch (e) {}
    });
  } catch (e) {}
  
  console.log('[PDF Polyfill] PDF-specific Promise.withResolvers polyfill initialization completed');
})();

// 导出类型声明
declare global {
  interface PromiseConstructor {
    withResolvers<T = any>(): {
      promise: Promise<T>;
      resolve: (value: T | PromiseLike<T>) => void;
      reject: (reason?: any) => void;
    };
  }
}

export {};